import { useEffect, useState } from 'react';
import { Menu } from '@mantine/core';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Bell,
  Command,
  LogOut,
  Menu as MenuIcon,
  Search,
  Settings,
  User as UserIcon,
} from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { useCommandPalette } from '../../store/useCommandPalette';
import { colors } from '../../theme/tokens';
import { Drawer, ThemeToggle } from '../ui';
import { NavList } from './NavList';
import brandMark from '../../assets/brand/cyberelites-mark.png';

/**
 * ForgeShell — application chrome, v3 clean professional SaaS.
 * A calm sidebar (clearly grouped nav, animated active highlight), a clean top
 * bar (search · theme toggle · notifications · identity), and a generous content
 * area. Below `md` the sidebar collapses to a hamburger-driven Drawer sharing the
 * same <NavList>. The information architecture lives here + in each page's
 * <PageHeader>, so the operator always knows where they are.
 */
export function ForgeShell({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? 'trainee';
  const email = user?.email ?? 'operator@forge';
  const initials = email.slice(0, 2).toUpperCase();
  const name = email.split('@')[0]!.split('.')[0]!;

  const openPalette = useCommandPalette((s) => s.setOpen);

  const [drawerOpen, setDrawerOpen] = useState(false);
  // Close the mobile drawer whenever the route changes (link tap or back nav).
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-void font-body text-txt">
      {/* A11y: first tab stop — lets keyboard users jump the ~15-item sidebar
          on every navigation. Visually hidden until focused (see .skip-link). */}
      <a href="#main" className="skip-link">
        Skip to main content
      </a>

      {/* ───────────────────────── Sidebar (md+) ───────────────────────── */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-bd bg-deep md:flex">
        {/* Brand */}
        <div className="flex h-14 shrink-0 items-center gap-2.5 px-5">
          <img src={brandMark} alt="" className="size-8 object-contain" />
          <div className="leading-none">
            <div className="font-display text-[15px] font-extrabold tracking-tight text-txt-bright">
              CyberElites
            </div>
            <div className="mt-0.5 text-[10px] font-medium text-txt-muted">Cyber Range</div>
          </div>
        </div>

        {/* Nav */}
        <NavList
          pathname={pathname}
          layoutId="nav-active-sidebar"
          className="flex-1 overflow-y-auto px-3 py-3"
        />

        {/* Identity */}
        <div className="shrink-0 border-t border-bd p-3">
          <IdentityMenu position="top-start">
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg p-2 text-left transition-colors hover:bg-card"
            >
              <span className="flex size-8 items-center justify-center rounded-full bg-neon text-[11px] font-bold text-void">
                {initials}
              </span>
              <span className="min-w-0 leading-tight">
                <span className="block truncate text-[13px] font-semibold capitalize text-txt">{name}</span>
                <span className="block truncate text-[11px] capitalize text-txt-muted">
                  {role.replace('_', ' ')}
                </span>
              </span>
            </button>
          </IdentityMenu>
        </div>
      </aside>

      {/* ───────────────────────── Mobile nav Drawer (below md) ───────────────────────── */}
      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        position="left"
        size={260}
        title={
          <span className="flex items-center gap-2.5">
            <img src={brandMark} alt="" className="size-7 object-contain" />
            <span className="font-display text-[15px] font-extrabold tracking-tight text-txt-bright">
              CyberElites
            </span>
          </span>
        }
      >
        <NavList
          pathname={pathname}
          layoutId="nav-active-drawer"
          onNavigate={() => setDrawerOpen(false)}
        />
      </Drawer>

      {/* ───────────────────────── Main column ───────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-bd bg-deep px-4 sm:px-6">
          {/* hamburger (below md) */}
          <button
            type="button"
            aria-label="Open navigation"
            onClick={() => setDrawerOpen(true)}
            className="flex size-8 cursor-pointer items-center justify-center rounded-lg border border-bd bg-card text-txt-2 transition-colors hover:border-bd-bright hover:text-txt md:hidden"
          >
            <MenuIcon className="size-4" />
          </button>

          {/* mobile brand */}
          <img src={brandMark} alt="CyberElites" className="size-8 object-contain md:hidden" />

          <button
            type="button"
            onClick={() => openPalette(true)}
            className="hidden w-72 cursor-pointer items-center gap-2.5 rounded-lg border border-bd bg-card px-3 py-2 text-left transition-colors hover:border-bd-bright sm:flex"
          >
            <Search className="size-4 text-txt-muted" />
            <span className="flex-1 text-[13px] text-txt-muted">Search ranges, scenarios…</span>
            <span className="flex items-center gap-0.5 rounded-md border border-bd bg-panel px-1.5 py-0.5 text-[10px] font-medium text-txt-muted">
              <Command className="size-3" />K
            </span>
          </button>

          <div className="ml-auto flex items-center gap-2">
            {/* mobile search icon (below sm) */}
            <button
              type="button"
              aria-label="Search"
              onClick={() => openPalette(true)}
              className="flex size-8 cursor-pointer items-center justify-center rounded-lg border border-bd bg-card text-txt-2 transition-colors hover:border-bd-bright hover:text-txt sm:hidden"
            >
              <Search className="size-4" />
            </button>
            <ThemeToggle />
            <button
              type="button"
              aria-label="Notifications"
              className="relative flex size-8 cursor-pointer items-center justify-center rounded-lg border border-bd bg-card text-txt-2 transition-colors duration-150 hover:border-bd-bright hover:text-txt"
            >
              <Bell className="size-4" />
              <span className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-neon ring-2 ring-card" />
            </button>
            <span className="mx-1 hidden h-6 w-px bg-bd sm:block" />
            <IdentityMenu position="bottom-end">
              <button
                type="button"
                aria-label="Account menu"
                className="flex cursor-pointer items-center gap-2 rounded-lg p-1 transition-colors hover:bg-card"
              >
                <span className="flex size-8 items-center justify-center rounded-full bg-neon text-[11px] font-bold text-void">
                  {initials}
                </span>
                <span className="hidden leading-tight lg:block">
                  <span className="block text-left text-[13px] font-semibold capitalize text-txt">{name}</span>
                  <span className="block text-left text-[10.5px] capitalize text-txt-muted">{role.replace('_', ' ')}</span>
                </span>
              </button>
            </IdentityMenu>
          </div>
        </header>

        <main id="main" tabIndex={-1} className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}

/**
 * IdentityMenu — account dropdown (Profile / Settings / Sign out). Wraps a
 * caller-supplied trigger as the `Menu.Target`. Sign-out clears the auth store
 * then hard-navigates to /login so the root `beforeLoad` re-runs with cleared
 * auth (SPA navigation alone would leave the previous route mounted on stale
 * data). Reused from the former UserMenu.
 */
function IdentityMenu({
  children,
  position,
}: {
  children: React.ReactNode;
  position: 'top-start' | 'bottom-end';
}) {
  const user = useAuthStore((s) => s.user);
  const baseSignOut = useAuthStore((s) => s.signOut);
  const navigate = useNavigate();

  const signOut = () => {
    baseSignOut();
    window.location.href = '/login';
  };

  return (
    <Menu shadow="md" width={220} position={position} withArrow={false}>
      <Menu.Target>{children}</Menu.Target>
      <Menu.Dropdown style={{ backgroundColor: colors.card, borderColor: 'var(--c-bd)' }}>
        <Menu.Label>
          <span className="block truncate text-[12px] font-semibold text-txt">
            {user?.email ?? 'operator@forge'}
          </span>
          <span className="block truncate text-[11px] capitalize text-txt-muted">
            {(user?.role ?? 'trainee').replace('_', ' ')}
          </span>
        </Menu.Label>
        <Menu.Divider />
        <Menu.Item leftSection={<UserIcon size={14} />} onClick={() => void navigate({ to: '/settings' })}>
          Profile
        </Menu.Item>
        <Menu.Item leftSection={<Settings size={14} />} onClick={() => void navigate({ to: '/settings' })}>
          Settings
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item color="red" leftSection={<LogOut size={14} />} onClick={signOut}>
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
